import {
  ALCOHOL,
  AROMA_INTENSITY,
  BODY,
  CLARITY,
  DEVELOPMENT,
  FINISH,
  FIVE_STEP,
  MOUSSE,
  NOSE_CONDITION,
  NOTE_SCHEMA,
  QUALITY,
  QUALITY_CRITERION,
  READINESS,
  RED_HUES,
  ROSE_HUES,
  SWEETNESS,
  VISUAL_INTENSITY,
  WHITE_HUES,
  WINE_COLOUR,
  isDescriptorId,
} from "@proef/domain";
import { z } from "zod";

const Descriptor = z
  .string()
  .refine(isDescriptorId, { message: "not a descriptor id in the proef lexicon" });

/**
 * Wire form of `StructuredNote`. Structural shape is enforced here; cross-field
 * rules (hue vs colour, tannin on reds, quality vs criteria) stay in the
 * domain's `validateNote`, which the worker calls after parsing.
 */
export const StructuredNote = z.object({
  schema: z.literal(NOTE_SCHEMA),
  sparkling: z.boolean(),
  appearance: z.object({
    clarity: z.enum(CLARITY),
    intensity: z.enum(VISUAL_INTENSITY),
    colour: z.enum(WINE_COLOUR),
    hue: z.enum([...WHITE_HUES, ...ROSE_HUES, ...RED_HUES]),
    other: z.string().max(280).optional(),
  }),
  nose: z.object({
    condition: z.enum(NOSE_CONDITION),
    intensity: z.enum(AROMA_INTENSITY),
    descriptors: z.array(Descriptor).max(40),
    development: z.enum(DEVELOPMENT),
  }),
  palate: z.object({
    sweetness: z.enum(SWEETNESS),
    acidity: z.enum(FIVE_STEP),
    tannin: z.enum(FIVE_STEP).optional(),
    alcohol: z.enum(ALCOHOL),
    body: z.enum(BODY),
    mousse: z.enum(MOUSSE).optional(),
    flavourIntensity: z.enum(FIVE_STEP),
    descriptors: z.array(Descriptor).max(40),
    finish: z.enum(FINISH),
  }),
  conclusion: z.object({
    quality: z.enum(QUALITY),
    criteriaMet: z.array(z.enum(QUALITY_CRITERION)).max(4),
    readiness: z.enum(READINESS),
  }),
  freeText: z.string().max(4000).optional(),
});
export type StructuredNote = z.infer<typeof StructuredNote>;
